import React from "react";
import PropTypes from "prop-types";
import GenericCard from "components/UI/GenericCard/GenericCard";
import DashedTooltip from "components/UI/DashedTooltip";
import { returnUpDownClassname } from "utils/helpers";

const ExpectedMoveCard = (props) => {
  const { metrics } = props.optionsResults;
  const {
    avgFinalReturn,
    medianFinalReturn,
    stdDevFinalReturn,
    finalReturnPercentiles,
    winReturnPercentiles,
    recommendedTargetMove,
    expectedMoveTiming,
  } = metrics;
  const { avgHoldWindowCalendarDays, avgHitCalendarDays } = expectedMoveTiming;

  return (
    <GenericCard
      title="Expected Move (for pricing options)"
      tooltipText="maxDays is trading bars, but an option's time-to-expiry (Black-Scholes T) is calendar days -- use avgHitCalendarDays (typical) or avgHoldWindowCalendarDays (ceiling) as T when pulling that expiry's chain. Then compare its IV-implied expected move -- roughly strike x IV x sqrt(T/365) -- against the numbers below. If this signal's historical move meaningfully exceeds what the option's IV is pricing in, there may be a real edge; if IV already prices in a bigger move than this signal typically produces, the option is expensive relative to what you can realistically expect."
    >
      <div>
        <DashedTooltip text="Median win, not average -- average gets pulled up by a few outsized winners, which would make for an overconfident target most wins won't actually reach. At least half your historical wins cleared this move.">
          <h5>Aim for</h5>
        </DashedTooltip>
        <h2 className={returnUpDownClassname(recommendedTargetMove)}>
          +{recommendedTargetMove}%
        </h2>
        <h5>
          within ~{avgHitCalendarDays} calendar days (typical time to hit)
        </h5>
      </div>
      <div className="pt-2 flex gap-8 flex-wrap">
        <div>
          <DashedTooltip text="Realistic base case: average underlying return across ALL complete signals, hits and misses both -- the honest 'what actually happens' number, not just the good outcomes.">
            <h5>Avg move (all signals)</h5>
          </DashedTooltip>
          <h3 className={returnUpDownClassname(avgFinalReturn)}>
            {avgFinalReturn}%
          </h3>
        </div>
        <div>
          <h5>Median move</h5>
          <h3 className={returnUpDownClassname(medianFinalReturn)}>
            {medianFinalReturn}%
          </h3>
        </div>
        <div>
          <DashedTooltip text="1 standard deviation of the realized move -- this is the number directly comparable to an at-the-money option's own IV-implied 1 SD expected move for the same expiry.">
            <h5>Std dev (1 SD move)</h5>
          </DashedTooltip>
          <h3>{stdDevFinalReturn}%</h3>
        </div>
      </div>
      <div className="pt-2">
        <h5>All signals -- range (p25/p50/p75)</h5>
        <h4>
          {finalReturnPercentiles.p25}% / {finalReturnPercentiles.p50}% /{" "}
          {finalReturnPercentiles.p75}%
        </h4>
      </div>
      <div className="pt-2">
        <DashedTooltip text="Upside case: when the signal DOES hit, this is the range of gains -- useful for gauging how aggressive a strike you could reasonably reach for.">
          <h5>Wins only -- range (p25/p50/p75)</h5>
        </DashedTooltip>
        <h4 className="green">
          {winReturnPercentiles.p25}% / {winReturnPercentiles.p50}% /{" "}
          {winReturnPercentiles.p75}%
        </h4>
      </div>
      <div className="pt-2 flex gap-8 flex-wrap">
        <div>
          <DashedTooltip text="Average calendar days from entry to the actual hit, among signals that hit -- the typical time an option would actually need to stay open. Often shorter than the full hold window.">
            <h5>Typical days to exit (T)</h5>
          </DashedTooltip>
          <h3>{avgHitCalendarDays} days</h3>
        </div>
        <div>
          <DashedTooltip text="Average calendar days spanning the full maxDays-bar hold window -- the ceiling if the signal takes the maximum time to play out (or doesn't hit at all).">
            <h5>Max hold window (T ceiling)</h5>
          </DashedTooltip>
          <h3>{avgHoldWindowCalendarDays} days</h3>
        </div>
      </div>
    </GenericCard>
  );
};

ExpectedMoveCard.propTypes = { optionsResults: PropTypes.object };

export default ExpectedMoveCard;
